import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { Loader2, TrendingUp, Users as UsersIcon, Trophy, Wallet, BarChart3, ChevronRight, AlertTriangle } from "lucide-react";
import { toast } from "sonner";
import api from "@/lib/api";
import { AppHeader } from "@/components/AppHeader";
import { formatEuro } from "@/components/AutoTextarea";

const fmtDays = (iso) => {
  if (!iso) return "–";
  try {
    const d = (Date.now() - new Date(iso).getTime()) / (1000 * 60 * 60 * 24);
    if (d < 1) return "heute";
    if (d < 2) return "gestern";
    return `vor ${Math.floor(d)}T`;
  } catch { return "–"; }
};

export default function TeamAnalytics() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    api.get("/analytics/team")
      .then((r) => setData(r.data))
      .catch((e) => toast.error(e.response?.data?.detail || "Laden fehlgeschlagen"))
      .finally(() => setLoading(false));
  }, []);

  if (loading) {
    return (
      <div className="min-h-screen bg-[#09090B] text-zinc-50">
        <AppHeader title="Team-Auswertung" back="/dashboard" />
        <div className="flex items-center justify-center py-32"><Loader2 className="animate-spin text-[#00E575]" size={32} /></div>
      </div>
    );
  }

  if (!data) return null;
  const tt = data.team_total;

  return (
    <div className="min-h-screen bg-[#09090B] text-zinc-50">
      <AppHeader title="Team" back="/dashboard" />
      <main className="max-w-6xl mx-auto px-4 py-6 space-y-5">
        <div>
          <div className="text-[12px] font-mono uppercase tracking-wider text-[#00E575] mb-1">Team-Auswertung</div>
          <h1 className="font-display text-2xl sm:text-3xl font-bold">Vertriebsteam · {tt.rep_count} Vertrieb</h1>
          <p className="text-zinc-400 mt-1 text-sm">Performance pro Vertrieb im Überblick. Klicken Sie auf eine Karte für die Detailauswertung.</p>
        </div>

        {/* Team KPIs */}
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-3" data-testid="team-kpis">
          <KpiCard icon={UsersIcon} label="Vertrieb" value={tt.rep_count} accent="text-zinc-200" />
          <KpiCard icon={BarChart3} label="Projekte gesamt" value={tt.projects} accent="text-zinc-200" />
          <KpiCard icon={Trophy} label="Gewonnen" value={tt.won} accent="text-[#00E575]" />
          <KpiCard icon={Wallet} label="Signed Volumen" value={`${formatEuro(tt.signed_volume)} €`} accent="text-[#00E575]" />
        </div>

        {/* Rep list */}
        {data.reps.length === 0 ? (
          <div className="bg-[#18181B] border border-zinc-800 rounded-2xl p-12 text-center text-zinc-400">
            <UsersIcon size={32} className="text-zinc-700 mx-auto mb-3" />
            Noch keine Vertrieb zugeordnet.<br />
            <Link to="/admin/users" className="text-[#00E575] hover:underline mt-2 inline-block">Benutzerverwaltung öffnen →</Link>
          </div>
        ) : (
          <div className="space-y-3" data-testid="rep-list">
            {data.reps.map((r) => {
              const conversionPct = r.projects_total > 0
                ? Math.round((r.projects_won / r.projects_total) * 100)
                : 0;
              const signedPct = r.offers_total > 0
                ? Math.round((r.offers_signed / r.offers_total) * 100)
                : 0;
              return (
                <Link
                  key={r.user.id}
                  to={`/manager/team/${r.user.id}`}
                  data-testid={`rep-card-${r.user.id}`}
                  className="block bg-[#18181B] hover:bg-zinc-800/30 border border-zinc-800 hover:border-[#00E575]/40 rounded-2xl p-5 transition"
                >
                  <div className="flex items-center justify-between gap-3 mb-3">
                    <div className="min-w-0">
                      <div className="font-display font-bold text-[17px] text-white truncate">
                        {r.user.first_name} {r.user.last_name}
                      </div>
                      <div className="text-[12px] text-zinc-400 truncate font-medium">
                        {r.user.email} · letzte Aktivität {fmtDays(r.last_activity_at)}
                      </div>
                    </div>
                    <ChevronRight size={20} className="text-zinc-500 flex-shrink-0" />
                  </div>

                  <div className="grid grid-cols-2 sm:grid-cols-4 gap-2">
                    <MiniStat label="Projekte" value={r.projects_total} />
                    <MiniStat label="Gewonnen" value={r.projects_won} accent="text-[#00E575]" sub={`${conversionPct}%`} />
                    <MiniStat label="Angebote signed" value={`${r.offers_signed}/${r.offers_total}`} sub={`${signedPct}%`} />
                    <MiniStat label="Signed €" value={formatEuro(r.signed_volume_net)} accent="text-[#00E575]" sub="netto" />
                  </div>
                </Link>
              );
            })}
          </div>
        )}
      </main>
    </div>
  );
}

function KpiCard({ icon: Icon, label, value, accent }) {
  return (
    <div className="bg-[#18181B] border border-zinc-800 rounded-2xl p-4">
      <div className="flex items-center justify-between mb-2">
        <div className="text-[12px] uppercase tracking-widest text-zinc-300 font-semibold font-bold">{label}</div>
        <Icon size={16} className="text-zinc-500" />
      </div>
      <div className={`font-display font-bold text-2xl ${accent}`}>{value}</div>
    </div>
  );
}

function MiniStat({ label, value, accent = "text-zinc-100", sub = null }) {
  return (
    <div className="bg-[#09090B]/60 rounded-lg px-3 py-2">
      <div className="text-[12px] uppercase tracking-widest text-zinc-300 font-semibold font-bold">{label}</div>
      <div className={`font-display font-bold text-[15px] ${accent} truncate`}>{value}</div>
      {sub && <div className="text-[12px] text-zinc-300 font-mono">{sub}</div>}
    </div>
  );
}
